import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppModal from '../common/AppModal';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_CONN } from '../../hooks/useOwnConnSession';
import { formatApiError } from '../../utils/formatters';
import axios from 'axios';
import { FiRefreshCw, FiPlay, FiCheckCircle, FiSearch, FiTerminal, FiAlertTriangle, FiInfo, FiClock, FiGrid, FiCode, FiChevronRight, FiChevronDown, FiChevronUp, FiFolder, FiSave, FiPackage } from 'react-icons/fi';
import dbQueries from '../../config/dbQueries';
import { parsePlsqlCreate } from '../../utils/plsqlCreate';
import { lineDiff, withContext, normalizeSource } from '../../utils/lineDiff';
import { useConfirm } from '../ConfirmDialog';
import SQLEditor from '../SQLEditor';
import VerticalSplit from '../common/VerticalSplit';
import { usePrompt } from '../PromptDialog';
import { lintPlsql } from '../../utils/plsqlLint';
import { saveTextFile, rememberOpenedFile, canWriteDisk, baseNameOf } from '../../utils/desktopFile';
import { serializeProject, parseProjectFile, PROJECT_EXT, PROJECT_EXTS, isProjectFileName } from '../../utils/projectFile';
import FileTabs, { ProjectPicker } from '../FileTabs';
import EditorSearchBar, { useEditorSearch } from '../EditorSearchBar';
import './PlsqlEditorModal.css';

// 아래 결과 칸 — 기억 키 · 기본/최소 높이 · 막대 두께(App.css .Resizer.horizontal 5px + 테두리) · 접혔을 때 탭 줄 높이
const RESULT_PANE_KEY = 'livesql.plsqlEditor.resultPane';
const RESULT_DEFAULT_H = 220;
const RESULT_MIN_H = 80;
const RESIZER_H = 7;
const RESULT_COLLAPSED_H = 32;

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');

// 표시 순서 — 자주 다루는 순서(호출형 → 정의형)
const TYPE_ORDER = ['PROCEDURE', 'FUNCTION', 'PACKAGE', 'PACKAGE BODY', 'TRIGGER'];

// 충돌 기준선의 열쇠 — 소유자·유형·이름이 같아야 같은 객체다
const plsqlKey = (owner, type, name) => `${owner}|${type}|${name}`;

// CREATE 문 해석은 utils/plsqlCreate.js 한 곳이다 — GET_DDL 이 주는 EDITIONABLE·"소유자"."이름" 까지 읽는다.
const BLOCK_RE = /^\s*(BEGIN|DECLARE)\b/i;

const ANON_BLOCK_TEMPLATE = 'BEGIN\n  \nEND;';

/**
 * 탭의 고침 표시(*) — DB(또는 파일) 기준선과 다르고, 마지막으로 파일·프로젝트에 **저장한 원문**과도 다를 때만.
 * 객체 탭은 저장해도 기준선(DB)을 옮기지 않으므로 savedText 로 표시만 내린다.
 */
export function isDocDirty(d) {
  const x = d || {};
  const src = x.sourceText || '';
  if (src === (x.originalSourceText || '')) return false;
  return x.savedText == null || src !== x.savedText;
}

/** 프로젝트 저장이 끝난 탭에 「저장한 원문」 을 적는다 — savedById: 탭 id → 저장한 원문 */
export function markTabSaved(tb, savedById) {
  if (!savedById.has(tb.id)) return tb;
  const d = tb.doc || {};
  const text = savedById.get(tb.id);
  // 객체가 아닌 탭은 파일이 기준(기준선도 옮김), 객체 탭은 DB 기준선을 두고 저장한 원문만
  return { ...tb, doc: { ...d, savedText: text, ...(d.selectedObject ? {} : { originalSourceText: text }) } };
}

/**
 * 객체 탭 이름 바꾸기 — **표시 이름(label)만** 바꾼다(2026-11-02 요청). 컴파일·충돌 확인·백업이 보는 객체
 * (doc.selectedObject · objectKey)는 그대로다. 탭 줄이 붙이는 .sql 은 떼고, 객체 이름으로 되돌리거나 비우면
 * 표시 이름을 지운다.
 */
export function renameObjectTab(tb, obj, name) {
  const label = String(name || '').replace(/\.sql$/i, '').trim();
  const next = { ...tb };
  if (!label || label === obj.name) delete next.label; else next.label = label;
  return next;
}
// 열린 탭을 연결별로 기억한다 — 창을 닫았다 열거나 앱을 다시 켜도 이어 간다(값은 이 PC 의 브라우저 저장소에만)
const TABS_STORAGE_PREFIX = 'livesql_plsql_tabs:';
const FILE_ACCEPT = `.sql,.pls,.pkb,.pks,.plsql,.prc,.fnc,.trg,.txt,${PROJECT_EXTS.join(',')}`;
function loadSavedTabs(connId) {
  try { const v = JSON.parse(localStorage.getItem(TABS_STORAGE_PREFIX + connId) || 'null'); return v && Array.isArray(v.tabs) ? v : null; }
  catch (_e) { return null; }
}

// PACKAGE/PACKAGE BODY 소스 내부 서브프로그램 개요(outline) — 줄 단위 선언부만 훑는 가벼운 휴리스틱.
// (전체 파서가 아니라 "PROCEDURE/FUNCTION 로 시작하는 줄"만 잡아내므로 중첩 블록 안의 지역 선언은
//  제외되지 않을 수 있음 — 점프 용도로는 충분하지만 완벽한 심볼 목록은 아님.)
// 이름 글자는 유니코드 전부(한글 이름의 서브프로그램이 개요에서 잘리지 않게 — utils/plsqlCreate 와 같은 집합)
const SUBPROGRAM_RE = /^\s*(PROCEDURE|FUNCTION)\s+"?([\p{L}\p{N}\p{M}_$#]+)"?/iu;

// PL/SQL 로컬 변수 선언 시 사용할 타입 — ALL_ARGUMENTS.DATA_TYPE 을 대략적으로 매핑.
function plsqlVarType(dataType) {
  const t = (dataType || '').toUpperCase();
  if (/^(NUMBER|INTEGER|PLS_INTEGER|BINARY_INTEGER|FLOAT|DEC|NUMERIC|SIMPLE_INTEGER)/.test(t)) return 'NUMBER';
  if (t === 'DATE') return 'DATE';
  if (t.startsWith('TIMESTAMP')) return 'TIMESTAMP';
  if (t === 'BOOLEAN' || t === 'PLS_BOOLEAN') return 'BOOLEAN';
  if (t === 'CLOB') return 'CLOB';
  return 'VARCHAR2(4000)';
}

// 사용자가 입력한 문자열 값을 데이터 타입에 맞는 PL/SQL 리터럴로 변환.
function formatArgLiteral(dataType, rawValue) {
  const v = (rawValue ?? '').trim();
  if (v === '') return 'NULL';
  const t = (dataType || '').toUpperCase();
  if (/^(NUMBER|INTEGER|PLS_INTEGER|BINARY_INTEGER|FLOAT|DEC|NUMERIC|SIMPLE_INTEGER)/.test(t)) {
    return /^-?\d+(\.\d+)?$/.test(v) ? v : `'${v.replace(/'/g, "''")}'`; // 숫자 형식이 아니면 안전하게 문자열로 폴백
  }
  if (t === 'DATE') return `TO_DATE('${v.replace(/'/g, "''")}', 'YYYY-MM-DD')`;
  if (t.startsWith('TIMESTAMP')) return `TO_TIMESTAMP('${v.replace(/'/g, "''")}', 'YYYY-MM-DD HH24:MI:SS')`;
  if (t === 'BOOLEAN' || t === 'PLS_BOOLEAN') return /^(true|false)$/i.test(v) ? v.toUpperCase() : 'NULL';
  return `'${v.replace(/'/g, "''")}'`;
}

// args: [{name, dataType, inOut}], values: {name: '사용자 입력값'}
// IN 파라미터는 입력값을 리터럴로 바로 대입하고, OUT/IN OUT 파라미터는 지역 변수를 선언해
// 호출 후 DBMS_OUTPUT 으로 값을 출력한다(IN OUT 은 초기값도 입력값으로 채움).
function isRefCursorType(dataType) {
  return /REF\s*CURSOR/i.test(dataType || '');
}

function buildTestCallSkeleton(schemaName, obj, args, values = {}, t) {
  const qualified = `${schemaName}.${obj.name}`;
  const cursorArgs = args.filter(a => isRefCursorType(a.dataType));
  const outArgs = args.filter(a => a.inOut !== 'IN' && !isRefCursorType(a.dataType));

  const declLines = [];
  if (obj.type === 'FUNCTION') {
    declLines.push('  v_result VARCHAR2(4000); -- ' + t('monitoring:plsql.resultVarDecl'));
  }
  outArgs.forEach(a => {
    const varType = plsqlVarType(a.dataType);
    const isInOut = /IN\s*\/?\s*OUT/i.test(a.inOut || '');
    declLines.push(isInOut
      ? `  v_${a.name} ${varType} := ${formatArgLiteral(a.dataType, values[a.name])};`
      : `  v_${a.name} ${varType};`);
  });

  const paramList = args.map(a => {
    if (isRefCursorType(a.dataType)) return `${a.name} => :cur_${a.name}`;
    const expr = a.inOut === 'IN' ? formatArgLiteral(a.dataType, values[a.name]) : `v_${a.name}`;
    return `${a.name} => ${expr}`;
  }).join(',\n    ');
  const callArgs = args.length > 0 ? `(\n    ${paramList}\n  )` : '';

  const printLines = outArgs.map(a => `  DBMS_OUTPUT.PUT_LINE('${a.name} => ' || v_${a.name});`);
  const declBlock = declLines.length > 0 ? `DECLARE\n${declLines.join('\n')}\n` : '';
  const bodyCall = obj.type === 'FUNCTION'
    ? `  v_result := ${qualified}${callArgs};\n  DBMS_OUTPUT.PUT_LINE('result: ' || v_result);\n`
    : `  ${qualified}${callArgs};\n`;

  const text = `${declBlock}BEGIN\n${bodyCall}${printLines.join('\n')}${printLines.length ? '\n' : ''}END;`;

  const binds = {};
  cursorArgs.forEach(a => { binds[`cur_${a.name}`] = { dir: 'OUT', type: 'CURSOR' }; });

  return { text, binds };
}

const PlsqlEditorModal = ({ isOpen, onClose, connections = [], defaultConnectionId }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  // 이 창이 **세션 하나를 붙들고** 있어야 컴파일과 실행이 같은 세션에서 일어난다.
  // 예전에는 요청마다 다른 세션이라 DBMS_OUTPUT·패키지 상태·ALTER SESSION 이 이어지지 않았다.
  const mon = useOwnConnSession({
    isOpen, connections, defaultConnectionId, label: 'plsql-editor',
    filter: ORACLE_CONN,
  });
  const oracleConns = mon.conns;
  const selectedConnId = mon.connId;
  const { run, params: monParams } = mon;
  // 호출 모양을 그대로 두고 줄만 세운다 — 한 세션은 한 번에 한 문장이다
  const runPost = useCallback((url, body) => run(() => axios.post(url, body)), [run]);

  const [schemaName, setSchemaName] = useState('');

  const [objects, setObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [objectsError, setObjectsError] = useState(null);
  const [objectFilter, setObjectFilter] = useState('');

  const [selectedObject, setSelectedObject] = useState(null);
  const [sourceText, setSourceText] = useState('');
  const [originalSourceText, setOriginalSourceText] = useState('');
  // 파일·프로젝트로 **저장한** 원문. 객체 탭의 편집 기준선(originalSourceText)은 DB 라 저장해도 바꾸지 않는다 —
  // 바꾸면 고친 원문을 CREATE 로 못 읽을 때 저장된 옛 소스를 다시 컴파일하는 길(!isDirty)이 열린다.
  // 그래서 탭의 「*」 만 이것으로도 내린다(2026-11-02 신고: 프로젝트를 저장해도 * 가 남았다).
  const [savedText, setSavedText] = useState(null);
  const [objectLoading, setObjectLoading] = useState(false);

  const [testCallText, setTestCallText] = useState('');
  const [testCallArgs, setTestCallArgs] = useState([]); // [{name, dataType, inOut}] — 바인드 변수 입력 폼용
  const [testCallValues, setTestCallValues] = useState({}); // {argName: t('monitoring:plsql.inputs')}
  const [testCallBinds, setTestCallBinds] = useState({}); // REF CURSOR 파라미터 — 실행 시 bindVariables 로 전달

  // 의존성 트리(ALL_DEPENDENCIES)
  const [depLoading, setDepLoading] = useState(false);
  const [depError, setDepError] = useState(null);
  const [dependsOn, setDependsOn] = useState(null);
  const [dependents, setDependents] = useState(null);

  // 로컬 편집 히스토리 — 컴파일 성공 시점 스냅샷
  const [showHistory, setShowHistory] = useState(false);
  // 테스트 호출(실행) 영역 — **기본은 접힘**(사용자 요청 2026-09-28). 편집기 자리를 먼저 쓰게 한다.
  // 접혀 있어도 실행(F5)은 이 호출문으로 돈다 — 그래서 접힌 머리줄에 호출문 첫 줄을 보여 준다.
  const [testCallOpen, setTestCallOpen] = useState(false);
  // ── 덮어쓰기 충돌 확인 (2026-09-29) ───────────────────────
  // DB 소스 **기준선**: 열 때 GET_DDL 로 받은 것 / 이 창이 컴파일한 직후 다시 받은 것.
  // 편집 기준선(originalSourceText, 「수정됨」 표시용)과 따로 둔다 — 내가 보낸 원문과 GET_DDL 이
  // 돌려주는 모양이 달라(EDITIONABLE·따옴표 등) 그것으로 비교하면 두 번째 컴파일부터 늘 충돌이 난다.
  const dbBaselineRef = useRef(null);   // { key, ddl }
  // 이 창이 **마지막으로 써 넣은** DB 소스. 이것과 같으면 덮어쓰기 전 백업을 하지 않는다(내 것이다).
  const writtenRef = useRef(null);      // { key, ddl }
  const [conflict, setConflict] = useState(null); // { reason, label, current, baseline, mine }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);

  const [compiling, setCompiling] = useState(false);
  const [compileErrors, setCompileErrors] = useState(null);
  const [compileError, setCompileError] = useState(null);

  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState(null);
  // 예외가 나기 전까지 찍힌 DBMS_OUTPUT — "어디까지 실행됐나" 가 런타임 진단의 핵심이라
  // 백엔드가 실패 응답에도 함께 실어 보낸다.
  const [runErrorOutput, setRunErrorOutput] = useState(null);

  const [activeResultTab, setActiveResultTab] = useState('compile');

  // 일괄 재컴파일(INVALID 오브젝트)
  const [bulkRecompiling, setBulkRecompiling] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkResult, setBulkResult] = useState(null);

  // 패키지 서브프로그램 개요 점프용 — SQLEditor 내부 CodeMirror EditorView 참조
  const mainEditorViewRef = useRef(null);
  // 툴바 검색 창 — SQL 편집과 같은 컴포넌트(EditorSearchBar). 편집기 Ctrl+F · F3 이 이것으로 온다
  const editorSearch = useEditorSearch(mainEditorViewRef);

  // ── 탭 (2026-10-02, 요청 「SQL 에디터처럼 프로젝트·파일 탭·저장·불러오기」) ─────────────────────
  // 이 창의 상태(편집 중인 원문·DB 기준선·호출문·결과)는 **지금 보는 탭 하나**의 것이다. 다른 탭으로 갈 때
  // 지금 것을 탭에 접어 넣고(snapshotDoc), 갈 탭의 것을 펼친다(restoreDoc). 상태를 탭별 배열로 바꾸면
  // 컴파일·충돌 확인·실행 코드 전부를 고쳐야 해서, 검증된 그 코드를 그대로 두는 쪽을 택했다.
  // 컴파일·실행·불러오는 중에는 탭을 못 옮긴다 — 결과가 다른 탭에 떨어지면 안 된다.
  const tabSeqRef = useRef(1);
  const makeTab = (extra) => ({ id: `pt_${Date.now().toString(36)}_${tabSeqRef.current++}`, kind: 'blank', doc: null, ...(extra || {}) });
  // 처음 탭도 `+` 로 연 탭과 같은 「스크립트 1」 이다 — 예전엔 편집 영역 없이 안내만 있는 빈 탭이라 바로 쓸 수 없었다(2026-10-05)
  const anonSeqRef = useRef(1);
  const initialTabRef = useRef(null);
  if (!initialTabRef.current) initialTabRef.current = makeTab({ kind: 'anon', anonNo: anonSeqRef.current++ });
  const [tabs, setTabs] = useState(() => [initialTabRef.current]);
  const [activeTabId, setActiveTabId] = useState(() => initialTabRef.current.id);
  const fileInputRef = useRef(null);
  // PL/SQL 프로젝트 — SQL 편집기와 같은 모양(2026-10-02 요청): 탭 줄 왼쪽의 선택 상자에서
  // 「개별 스크립트」(projectId 없음)와 연 프로젝트들을 오간다. 탭은 projectId 로 묶이고, 보이는 것은
  // 고른 묶음의 탭뿐이다. 묶음마다 마지막으로 보던 탭을 기억한다.
  const [plsqlProjects, setPlsqlProjects] = useState([]);   // [{ id, name, diskPath }]
  const [activeProjectId, setActiveProjectId] = useState(null);
  const projSeqRef = useRef(1);
  const makeProjectId = () => `pp_${Date.now().toString(36)}_${projSeqRef.current++}`;
  const lastActiveRef = useRef({});
  const projKey = (pid) => pid || '__default__';
  const inProject = (tb, pid) => (tb.projectId || null) === (pid || null);

  const snapshotDoc = () => ({
    schemaName, selectedObject, sourceText, originalSourceText, savedText,
    testCallText, testCallArgs, testCallValues, testCallBinds,
    compileErrors, compileError, runResult, runError, runErrorOutput,
    dependsOn, dependents, depError, conflict, activeResultTab,
    dbBaseline: dbBaselineRef.current, written: writtenRef.current,
  });
  const restoreDoc = (d) => {
    const x = d || {};
    if (x.schemaName && x.schemaName !== schemaName) setSchemaName(x.schemaName);
    setSelectedObject(x.selectedObject || null);
    setSourceText(x.sourceText || '');
    setOriginalSourceText(x.originalSourceText || '');
    setSavedText(x.savedText ?? null);
    setTestCallText(x.testCallText || '');
    setTestCallArgs(x.testCallArgs || []);
    setTestCallValues(x.testCallValues || {});
    setTestCallBinds(x.testCallBinds || {});
    setCompileErrors(x.compileErrors ?? null);
    setCompileError(x.compileError ?? null);
    setRunResult(x.runResult ?? null);
    setRunError(x.runError ?? null);
    setRunErrorOutput(x.runErrorOutput ?? null);
    setDependsOn(x.dependsOn ?? null);
    setDependents(x.dependents ?? null);
    setDepError(x.depError ?? null);
    setConflict(x.conflict ?? null);
    setActiveResultTab(x.activeResultTab || 'compile');
    dbBaselineRef.current = x.dbBaseline || null;
    writtenRef.current = x.written || null;
    setShowHistory(false);
    setHistoryList([]);
  };
  const tabBusy = compiling || running || objectLoading || bulkRecompiling;
  const activeTab = tabs.find(tb => tb.id === activeTabId) || tabs[0];

  const switchTab = (id) => {
    if (id === activeTabId || tabBusy) return;
    const target = tabs.find(tb => tb.id === id);
    if (!target) return;
    setTabs(prev => prev.map(tb => (tb.id === activeTabId ? { ...tb, doc: snapshotDoc() } : tb)));
    restoreDoc(target.doc);
    setActiveTabId(id);
  };
  /** 새 탭을 열고 그 탭으로 간다 — 지금 탭은 접어 둔다 */
  const openNewTab = (meta, doc, opts) => {
    // 처음 내용을 탭에도 담아 둔다 — 여러 개를 한 번에 열면(프로젝트) 마지막 것만 화면 상태에 남기 때문이다
    const tb = makeTab({ projectId: activeProjectId, ...meta, doc: doc || null });
    setTabs(prev => {
      const snapped = prev.map(x => (x.id === activeTabId ? { ...x, doc: snapshotDoc() } : x));
      // + 로 연 탭은 SQL 편집기처럼 맨 앞(왼쪽)에 — 기존 탭은 오른쪽으로 밀린다
      return opts && opts.front ? [tb, ...snapped] : [...snapped, tb];
    });
    setActiveTabId(tb.id);
    restoreDoc(doc || null);
    return tb.id;
  };
  /** 지금 탭이 비어 있으면(아무것도 안 쓴 새 스크립트) 그 탭을 쓰고, 아니면 새 탭.
   *  옛 탭(저장된 것)은 익명 블록 틀(BEGIN … END;)로 시작했다 — 손대지 않았으면 그것도 빈 것으로 본다 */
  const activeIsBlank = () => (
    !selectedObject && (activeTab?.kind === 'blank' || activeTab?.kind === 'anon')
    && (sourceText === '' || sourceText === ANON_BLOCK_TEMPLATE) && sourceText === originalSourceText
  );
  const ensureFreshTab = (meta) => {
    if (activeIsBlank()) {
      setTabs(prev => prev.map(tb => (tb.id === activeTabId ? { ...tb, ...meta } : tb)));
      return activeTabId;
    }
    return openNewTab(meta);
  };
  const docOf = (tb) => (tb.id === activeTabId ? { selectedObject, sourceText, originalSourceText, savedText } : (tb.doc || {}));
  const tabIsDirty = (tb) => isDocDirty(docOf(tb));
  const tabHasContent = (tb) => {
    const d = docOf(tb);
    const text = String(d.sourceText || '');
    return !!d.selectedObject || (!!text.trim() && text !== ANON_BLOCK_TEMPLATE);
  };
  const tabLabel = (tb) => {
    const d = docOf(tb);
    // 객체 탭은 사람이 붙인 표시 이름(label)이 있으면 그것 — 컴파일 대상은 늘 selectedObject 다
    if (d.selectedObject) return tb.label || d.selectedObject.name;
    if (tb.fileName) return tb.fileName;
    // 「익명 블록」 이라 부르지 않는다 — 여기서 CREATE PROCEDURE … 로 이름 있는 객체도 만든다(2026-10-02 지적).
    // 본문이 CREATE 문이면 그 객체 이름이 탭 이름이고, 아니면 중립적인 「스크립트 N」.
    const created = parsePlsqlCreate(d.sourceText);
    if (created) return created.name;
    if (tb.kind === 'anon') return t('monitoring:plsql.scriptN', { n: tb.anonNo || 1 });
    return t('monitoring:plsql.newTab');
  };
  /** 편집기 위 제목 — 객체 탭은 그 객체, 아니면 본문이 무엇인지(CREATE 문 · 익명 블록 · 빈 스크립트) */
  const currentLabel = () => {
    if (selectedObject) return `${selectedObject.type} ${schemaName}.${selectedObject.name}`;
    const created = parsePlsqlCreate(sourceText);
    if (created) return t('monitoring:plsql.createLabel', { type: created.type, name: `${created.owner || schemaName}.${created.name}` });
    return sourceText.trim() ? t('monitoring:plsql.anonymousBlock') : t('monitoring:plsql.emptyScript');
  };
  const visibleTabs = tabs.filter(tb => inProject(tb, activeProjectId));
  /** 탭 닫기 — 하나든 여럿이든(우클릭 메뉴) 같은 길. 고친 탭이 있으면 한 번만 묻는다 */
  const closeTabs = async (ids) => {
    if (tabBusy) return;
    const targets = tabs.filter(x => ids.includes(x.id));
    if (targets.length === 0) return;
    const dirty = targets.filter(tabIsDirty);
    // 고치지 않았어도 내용이 있으면 묻는다(2026-11-04 사용자 요청). 빈 탭·빈 익명 블록 틀만 있는 탭은 묻지 않는다
    const filled = targets.filter(tb => !tabIsDirty(tb) && tabHasContent(tb));
    if (dirty.length > 0 || filled.length > 0) {
      const ok = await confirm({
        title: t(dirty.length > 0 ? 'monitoring:plsql.closeDirtyTitle' : 'common:ft.closeContentTitle'),
        message: dirty.length === 0
          ? t('common:ft.closeContentMessage', { count: filled.length, names: filled.map(tabLabel).join(', ') })
          : dirty.length === 1
          ? t('monitoring:plsql.closeDirty', { name: tabLabel(dirty[0]) })
          : t('common:ft.closeDirtyMessage', { count: dirty.length, names: dirty.map(tabLabel).join(', ') }),
        confirmText: t('monitoring:plsql.closeTab'),
        cancelText: t('monitoring:plsql.cancel'),
        variant: 'danger',
      });
      if (!ok) return;
    }
    const rest = tabs.filter(x => !ids.includes(x.id));
    if (!ids.includes(activeTabId)) { setTabs(rest); return; }
    // 다음 탭은 **같은 묶음** 안에서 — 다른 프로젝트의 탭으로 건너가면 선택 상자와 화면이 어긋난다
    const idx = visibleTabs.findIndex(x => x.id === activeTabId);
    const restVis = rest.filter(x => inProject(x, activeProjectId));
    if (restVis.length === 0) {
      const blank = makeTab({ projectId: activeProjectId });
      setTabs([...rest, blank]);
      setActiveTabId(blank.id);
      restoreDoc(null);
      return;
    }
    const next = restVis[Math.min(Math.max(idx, 0), restVis.length - 1)];
    setTabs(rest);
    setActiveTabId(next.id);
    restoreDoc(next.doc);
  };
  const closeTab = (id) => closeTabs([id]);
  const renameTab = (id, name) => {
    setTabs(prev => prev.map((tb) => {
      if (tb.id !== id) return tb;
      const obj = tb.id === activeTabId ? selectedObject : tb.doc?.selectedObject;
      return obj ? renameObjectTab(tb, obj, name) : { ...tb, fileName: name };
    }));
  };
  const reorderTab = (dragId, targetId, before) => {
    setTabs(prev => {
      const moving = prev.find(tb => tb.id === dragId);
      if (!moving) return prev;
      const without = prev.filter(tb => tb.id !== dragId);
      const at = without.findIndex(tb => tb.id === targetId);
      if (at < 0) return prev;
      const pos = before ? at : at + 1;
      return [...without.slice(0, pos), moving, ...without.slice(pos)];
    });
  };
  /** 프로젝트(또는 개별 스크립트) 바꾸기 — 그 묶음에서 마지막으로 보던 탭으로 간다 */
  const switchProject = (pid) => {
    const target = pid || null;
    if (target === activeProjectId || tabBusy) return;
    lastActiveRef.current[projKey(activeProjectId)] = activeTabId;
    const snapped = tabs.map(tb => (tb.id === activeTabId ? { ...tb, doc: snapshotDoc() } : tb));
    const group = snapped.filter(tb => inProject(tb, target));
    let next = group.find(tb => tb.id === lastActiveRef.current[projKey(target)]) || group[0];
    if (!next) { next = makeTab({ projectId: target }); snapped.push(next); }
    setTabs(snapped);
    setActiveProjectId(target);
    setActiveTabId(next.id);
    restoreDoc(next.doc);
  };
  /** 프로젝트 닫기 — 그 탭들을 닫고(지금 보던 것이면) 개별 스크립트로 돌아간다. 고친 탭이 있으면 묻는다 */
  const closeProject = async (pid) => {
    if (!pid || tabBusy) return;
    const dirty = tabs.filter(tb => tb.projectId === pid).filter(tabIsDirty);
    if (dirty.length > 0) {
      const ok = await confirm({
        title: t('common:ft.closeDirtyTitle'),
        message: t('common:ft.closeDirtyMessage', { count: dirty.length, names: dirty.map(tabLabel).join(', ') }),
        confirmText: t('common:ft.closeProject'),
        cancelText: t('monitoring:plsql.cancel'),
        variant: 'danger',
      });
      if (!ok) return;
    }
    const remaining = tabs.filter(tb => tb.projectId !== pid);
    setPlsqlProjects(prev => prev.filter(p => p.id !== pid));
    delete lastActiveRef.current[pid];
    if (activeProjectId !== pid) { setTabs(remaining); return; }
    const group = remaining.filter(tb => inProject(tb, null));
    let next = group.find(tb => tb.id === lastActiveRef.current[projKey(null)]) || group[0];
    if (!next) { next = makeTab({ projectId: null }); remaining.push(next); }
    setTabs(remaining);
    setActiveProjectId(null);
    setActiveTabId(next.id);
    restoreDoc(next.doc);
  };

  // 연결이 바뀌면 그 연결의 탭으로 — 객체 탭은 연결에 묶여 있다(다른 연결의 객체를 이 연결에 컴파일하면 안 된다)
  useEffect(() => {
    if (!selectedConnId) return;
    const saved = loadSavedTabs(selectedConnId);
    lastActiveRef.current = {};
    let projects = Array.isArray(saved?.projects) ? saved.projects.filter(p => p && p.id) : [];
    let list = saved ? saved.tabs.map(tb => ({ ...tb })) : [];
    // 옛 저장(프로젝트 하나·탭에 projectId 없음)은 「열린 탭 전부가 그 프로젝트」 였다 — 그 뜻대로 옮긴다
    if (!saved?.projects && saved?.project?.name) {
      const pid = 'pp_legacy';
      projects = [{ ...saved.project, id: pid }];
      list = list.map(tb => ({ ...tb, projectId: pid }));
    }
    const known = new Set(projects.map(p => p.id));
    list = list.map(tb => (tb.projectId && !known.has(tb.projectId) ? { ...tb, projectId: null } : tb));
    // 번호는 복원된 「스크립트 N」 다음부터 — 안 그러면 새 탭이 이미 있는 번호를 또 쓴다.
    // 옛 빈 탭(kind 'blank', 「새 탭」)은 「스크립트 N」 으로 — 이제 빈 탭도 바로 쓰는 새 스크립트다(2026-10-05)
    anonSeqRef.current = list.reduce((mx, tb) => Math.max(mx, Number(tb.anonNo) || 0), 0) + 1;
    list = list.map(tb => (tb.kind === 'blank' && !tb.fileName ? { ...tb, kind: 'anon', anonNo: anonSeqRef.current++ } : tb));
    setPlsqlProjects(projects);
    if (list.length > 0) {
      const act = list.find(tb => tb.id === saved.activeId) || list[0];
      setTabs(list);
      setActiveProjectId(act.projectId || null);
      setActiveTabId(act.id);
      restoreDoc(act.doc);
    } else {
      const blank = makeTab({ kind: 'anon', anonNo: anonSeqRef.current++ });
      setTabs([blank]);
      setActiveProjectId(null);
      setActiveTabId(blank.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnId]);

  // 열린 탭을 이 PC 에 기억한다 — 원문·기준선·이름만(결과·호출문은 다시 만들면 된다)
  useEffect(() => {
    if (!selectedConnId) return undefined;
    const timer = setTimeout(() => {
      const slim = (tb) => {
        const d = tb.id === activeTabId ? snapshotDoc() : (tb.doc || {});
        return {
          id: tb.id, projectId: tb.projectId || null, kind: tb.kind, objectKey: tb.objectKey, fileName: tb.fileName, label: tb.label, diskPath: tb.diskPath, anonNo: tb.anonNo,
          doc: { schemaName: d.schemaName, selectedObject: d.selectedObject || null, sourceText: d.sourceText || '', originalSourceText: d.originalSourceText || '', savedText: d.savedText ?? null },
        };
      };
      try {
        localStorage.setItem(TABS_STORAGE_PREFIX + selectedConnId, JSON.stringify({ tabs: tabs.map(slim), activeId: activeTabId, projects: plsqlProjects, activeProjectId }));
      } catch (_e) { /* 저장 공간이 없거나 막혀 있어도 편집은 된다 */ }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, activeTabId, sourceText, originalSourceText, savedText, selectedObject, schemaName, selectedConnId, plsqlProjects, activeProjectId]);

  // ── 실시간 점검 — 입력이 멎으면(0.3초) 구조만 본다(utils/plsqlLint). 오라클 컴파일러를 대신하지 않는다 ──
  const [lintIssues, setLintIssues] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => setLintIssues(lintPlsql(sourceText)), 300);
    return () => clearTimeout(timer);
  }, [sourceText]);
  const lintErrorCount = lintIssues.filter(i => i.severity === 'error').length;

  // ── 아래 결과 칸(실시간 점검·컴파일·실행·의존성) — 높이를 끌어 바꾸고 통째로 접는다(2026-11-04 시험 피드백).
  // 분할은 SQL 편집과 같은 VerticalSplit(CSS zoom 아래 좌표 보정 포함). 기억은 이 PC 의 localStorage — 실패해도 기본값으로 돈다.
  const [resultPane, setResultPane] = useState(() => {
    try {
      const v = JSON.parse(window.localStorage.getItem(RESULT_PANE_KEY) || 'null');
      if (v && typeof v === 'object') {
        const h = Number(v.height);
        return { height: Number.isFinite(h) ? Math.max(RESULT_MIN_H, Math.round(h)) : RESULT_DEFAULT_H, collapsed: !!v.collapsed };
      }
    } catch (_e) { /* 저장소를 못 쓰는 환경 */ }
    return { height: RESULT_DEFAULT_H, collapsed: false };
  });
  useEffect(() => {
    try { window.localStorage.setItem(RESULT_PANE_KEY, JSON.stringify(resultPane)); } catch (_e) { /* 무시 */ }
  }, [resultPane]);
  const splitHostRef = useRef(null);
  // VerticalSplit 은 위 칸 높이를 준다 — 결과 칸 높이 = 전체 − 위 칸 − 막대
  const onSplitChange = useCallback((topPx) => {
    const host = splitHostRef.current;
    if (!host) return;
    const total = host.clientHeight;   // clientHeight 는 CSS px(확대와 무관)
    const h = Math.max(RESULT_MIN_H, Math.round(total - topPx - RESIZER_H));
    setResultPane(p => (p.height === h ? p : { ...p, height: h }));
  }, []);
  const setResultCollapsed = useCallback((collapsed) => setResultPane(p => (p.collapsed === collapsed ? p : { ...p, collapsed })), []);
  // 컴파일·실행·충돌은 결과를 봐야 한다 — 접혀 있으면 편다(점검 결과의 갱신으로는 펴지 않는다)
  useEffect(() => {
    if (compiling || running || conflict) setResultCollapsed(false);
  }, [compiling, running, conflict, setResultCollapsed]);
  const pickResultTab = (tab) => { setActiveResultTab(tab); setResultCollapsed(false); };
  const jumpToLine = (line, col) => {
    const view = mainEditorViewRef.current;
    if (!view) return;
    try {
      const l = view.state.doc.line(Math.min(Math.max(line, 1), view.state.doc.lines));
      const pos = Math.min(l.from + Math.max((col || 1) - 1, 0), l.to);
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    } catch (_e) { /* 문서가 바뀌어 그 줄이 없다 */ }
  };

  // ── 파일 · 프로젝트 ──────────────────────────────────────────────
  const saveTabToFile = async () => {
    const tb = activeTab;
    const base = selectedObject ? (tb.label || selectedObject.name.toLowerCase()) : (tb.fileName ? tb.fileName : tabLabel(tb));
    const fileName = /\.[a-z0-9]+$/i.test(base) ? base : `${base}.sql`;
    const r = await saveTextFile({ fileName, content: sourceText, kind: 'sql', diskPath: tb.diskPath });
    if (!r || !r.ok) return;
    const savedName = r.path ? r.path.split(/[\\/]/).pop() : fileName;
    setTabs(prev => prev.map(x => (x.id === tb.id ? { ...x, fileName: savedName, ...(r.path ? { diskPath: r.path } : {}) } : x)));
    // DB 객체가 아닌 탭은 파일이 기준이다 — 저장했으면 고침 표시를 내린다.
    // 객체 탭의 기준은 DB 라 기준선은 두고 「저장한 원문」 만 적는다(탭의 * 가 내려간다)
    if (!selectedObject) setOriginalSourceText(sourceText);
    setSavedText(sourceText);
  };

  const openFiles = () => fileInputRef.current && fileInputRef.current.click();
  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = null;
    for (const f of files) {
      const text = await f.text();
      const diskPath = await rememberOpenedFile(f);
      if (isProjectFileName(f.name)) {
        let parsed;
        try { parsed = parseProjectFile(text, t); } catch (err) { setCompileError(err.message); continue; }
        // 이미 연 프로젝트 파일이면 그리로 간다 — 같은 파일을 두 묶음으로 열면 어느 쪽을 저장했는지 모른다
        const already = diskPath && plsqlProjects.find(p => p.diskPath === diskPath);
        if (already) { switchProject(already.id); continue; }
        const pid = makeProjectId();
        lastActiveRef.current[projKey(activeProjectId)] = activeTabId;
        if (parsed.files.length === 0) openNewTab({ projectId: pid });
        parsed.files.forEach((pf) => {
          const obj = pf.plsqlObject && pf.plsqlObject.name ? { type: pf.plsqlObject.type, name: pf.plsqlObject.name } : null;
          const content = pf.content || '';
          openNewTab(
            obj
              ? { kind: 'object', projectId: pid, objectKey: `${pf.plsqlObject.schema}.${obj.type}.${obj.name}`, ...(pf.name && pf.name !== obj.name ? { label: pf.name } : {}) }
              : { kind: 'file', projectId: pid, fileName: pf.name },
            // 원문은 파일의 것 — DB 와 같은지는 모른다. 기준선이 없으면 컴파일 때 「이미 있다」 로 비교해 묻는다
            { schemaName: obj ? pf.plsqlObject.schema : schemaName, selectedObject: obj, sourceText: content, originalSourceText: content },
          );
        });
        setPlsqlProjects(prev => [...prev, { id: pid, name: parsed.projectName || baseNameOf(f.name), diskPath: diskPath || null }]);
        setActiveProjectId(pid);
        continue;
      }
      openNewTab({ kind: 'file', fileName: f.name, ...(diskPath ? { diskPath } : {}) },
        { schemaName, sourceText: text, originalSourceText: text });
    }
  };

  // 지금 고른 묶음의 탭을 저장한다. 「개별 스크립트」 에서 저장하면 그 탭들이 새 프로젝트가 된다
  // (선택 상자에 이름이 생기고 다음 저장부터는 같은 파일에 쓴다).
  const saveProject = async (saveAs = false) => {
    const current = plsqlProjects.find(p => p.id === activeProjectId) || null;
    let name = current?.name || '';
    if (!canWriteDisk() && (saveAs || !name)) {
      const input = await prompt({
        title: t('monitoring:plsql.projectSave'), message: t('monitoring:plsql.projectNamePrompt'),
        defaultValue: name || 'plsql-project', placeholder: 'plsql-project',
      });
      if (!input) return;
      name = input.trim() || 'plsql-project';
    }
    const all = tabs.map(tb => (tb.id === activeTabId ? { ...tb, doc: snapshotDoc() } : tb));
    const files = all.filter(tb => inProject(tb, activeProjectId)).map((tb, i) => {
      const d = tb.doc || {};
      return {
        id: tb.id || `f${i}`,
        name: d.selectedObject ? (tb.label || d.selectedObject.name) : (tb.fileName || tabLabel(tb)),
        content: d.sourceText || '',
        bindVariables: {},
        // 프로젝트 파일 형식은 SQL 편집기와 같다 — 이 칸은 모르는 필드로 보존된다(utils/projectFile)
        ...(d.selectedObject ? { plsqlObject: { schema: d.schemaName || schemaName, type: d.selectedObject.type, name: d.selectedObject.name } } : {}),
      };
    });
    const json = serializeProject(files, name || 'plsql-project', { editor: 'plsql' });
    const r = await saveTextFile({ fileName: `${(name || 'plsql-project').replace(/[\\/:*?"<>|]/g, '_')}${PROJECT_EXT}`, content: json, kind: 'project', diskPath: current?.diskPath, saveAs });
    if (!r || !r.ok) return;
    // 저장한 탭의 고침 표시(*)를 내린다(2026-11-02 신고). 담은 것은 저장하던 순간의 원문(files)이다 —
    // 대화상자를 기다리는 동안 더 친 글은 저장되지 않았으니 그 탭은 여전히 「고침」 이다.
    const savedById = new Map(files.map(f => [f.id, f.content]));
    if (savedById.has(activeTabId)) {
      const text = savedById.get(activeTabId);
      setSavedText(text);
      // 객체가 아닌 탭은 파일이 기준이다(saveTabToFile 과 같다) — 객체 탭은 DB 기준선을 그대로 둔다
      if (!selectedObject && text === sourceText) setOriginalSourceText(text);
    }
    const mark = (tb) => (tb.id === activeTabId ? tb : markTabSaved(tb, savedById));
    const savedAs = { name: r.path ? baseNameOf(r.path) : name, diskPath: r.path || current?.diskPath || null };
    if (current) {
      setPlsqlProjects(prev => prev.map(p => (p.id === current.id ? { ...p, ...savedAs } : p)));
      setTabs(prev => prev.map(mark));
      return;
    }
    const pid = makeProjectId();
    setPlsqlProjects(prev => [...prev, { id: pid, ...savedAs }]);
    setTabs(prev => prev.map(tb => mark(inProject(tb, null) ? { ...tb, projectId: pid } : tb)));
    setActiveProjectId(pid);
  };

  const selectedConn = oracleConns.find(c => c.id === selectedConnId);
  // 컴파일 판단은 DB 기준선(isDirty), 「수정됨」 표시는 저장한 원문까지 본다(showDirty) — 둘을 섞지 말 것
  const isDirty = sourceText !== originalSourceText;
  const showDirty = isDocDirty({ sourceText, originalSourceText, savedText });
  const invalidObjects = useMemo(() => objects.filter(o => o.status !== 'VALID'), [objects]);

  // 연결이 바뀌면 스키마 기본값 = 로그인 계정, 나머지 상태 초기화
  useEffect(() => {
    if (!selectedConn) return;
    setSchemaName((selectedConn.user || '').toUpperCase());
    setObjects([]);
    setSelectedObject(null);
    setSourceText('');
    setOriginalSourceText('');
    setSavedText(null);
    setTestCallText('');
    setTestCallArgs([]);
    setTestCallValues({});
    setTestCallBinds({});
    setCompileErrors(null);
    setCompileError(null);
    setRunResult(null);
    setRunError(null);
    setBulkResult(null);
    setDependsOn(null);
    setDependents(null);
    setDepError(null);
    setShowHistory(false);
    setHistoryList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnId]);

  const loadObjects = useCallback(async () => {
    if (!selectedConnId || !schemaName) return;
    setObjectsLoading(true);
    setObjectsError(null);
    try {
      const res = await runPost(`${API_BASE}/sql-developer/execute`, {
        query: dbQueries.oracle.fetchPlsqlObjects(schemaName),
        ...monParams,
        usrid: 'standalone',
      });
      const rows = (res.data?.rows || []).map(r => ({
        name: r.OBJECT_NAME ?? r.object_name,
        type: r.OBJECT_TYPE ?? r.object_type,
        status: r.STATUS ?? r.status,
      }));
      setObjects(rows);
    } catch (err) {
      setObjectsError(formatApiError(err, t));
      setObjects([]);
    } finally {
      setObjectsLoading(false);
    }
  }, [runPost, monParams, selectedConnId, schemaName]);

  useEffect(() => {
    if (isOpen && selectedConnId && schemaName) loadObjects();
  }, [isOpen, selectedConnId, schemaName, loadObjects]);

  const handleGenerateTestCall = useCallback(async (obj) => {
    if (!selectedConnId || !schemaName) return;
    try {
      const res = await runPost(`${API_BASE}/sql-developer/execute`, {
        query: dbQueries.oracle.fetchPlsqlArguments(schemaName, obj.name),
        ...monParams,
        usrid: 'standalone',
      });
      const rows = res.data?.rows || [];
      const firstOverload = rows.length > 0 ? (rows[0].OVERLOAD ?? rows[0].overload ?? null) : null;
      const args = rows
        .filter(r => (r.OVERLOAD ?? r.overload ?? null) === firstOverload)
        .map(r => ({
          name: r.ARGUMENT_NAME ?? r.argument_name,
          dataType: r.DATA_TYPE ?? r.data_type,
          inOut: r.IN_OUT ?? r.in_out,
        }));
      setTestCallArgs(args);
      setTestCallValues({});
      const skeleton = buildTestCallSkeleton(schemaName, obj, args, {}, t);
      setTestCallText(skeleton.text);
      setTestCallBinds(skeleton.binds);
    } catch (_err) {
      // 파라미터 조회 실패(권한 등) — 빈 스켈레톤만 남기고 사용자가 직접 작성하게 둔다.
      setTestCallArgs([]);
      setTestCallValues({});
      const skeleton = buildTestCallSkeleton(schemaName, obj, [], {}, t);
      setTestCallText(skeleton.text);
      setTestCallBinds(skeleton.binds);
    }
  }, [runPost, monParams, selectedConnId, schemaName]);

  // 파라미터 입력 폼에 값을 채운 뒤 호출문에 반영
  const applyTestCallValues = useCallback(() => {
    if (!selectedObject) return;
    const skeleton = buildTestCallSkeleton(schemaName, selectedObject, testCallArgs, testCallValues, t);
    setTestCallText(skeleton.text);
    setTestCallBinds(skeleton.binds);
  }, [schemaName, selectedObject, testCallArgs, testCallValues]);

  const selectObject = async (obj) => {
    if (tabBusy) return;
    const objectKey = `${schemaName}.${obj.type}.${obj.name}`;
    const existing = tabs.find(tb => tb.objectKey === objectKey);
    if (existing) { switchTab(existing.id); return; }
    ensureFreshTab({ kind: 'object', objectKey, label: undefined });
    setSelectedObject(obj);
    setSourceText('');
    setOriginalSourceText('');
    setSavedText(null);
    setTestCallText('');
    setTestCallArgs([]);
    setTestCallValues({});
    setTestCallBinds({});
    setCompileErrors(null);
    setCompileError(null);
    setRunResult(null);
    setRunError(null);
    setDependsOn(null);
    setDependents(null);
    setDepError(null);
    setShowHistory(false);
    setHistoryList([]);
    setObjectLoading(true);
    try {
      const res = await runPost(`${API_BASE}/sql-developer/execute`, {
        query: dbQueries.oracle.fetchPlsqlSource(schemaName, obj.type, obj.name),
        ...monParams,
        usrid: 'standalone',
      });
      const row = res.data?.rows?.[0];
      const ddl = String(row?.DDL_TEXT ?? row?.ddl_text ?? '').trim();
      setSourceText(ddl);
      setOriginalSourceText(ddl);
      dbBaselineRef.current = { key: plsqlKey(schemaName, obj.type, obj.name), ddl };
      writtenRef.current = null;
      setConflict(null);
      if (obj.type === 'PROCEDURE' || obj.type === 'FUNCTION') {
        handleGenerateTestCall(obj);
      }
    } catch (err) {
      const msg = formatApiError(err, t);
      setSourceText(t('monitoring:plsql.sourceFailed', { message: msg }));
    } finally {
      setObjectLoading(false);
    }
  };

  // + 버튼 — 늘 새 탭(SQL 편집기의 + 와 같다). 지금 고른 프로젝트에 붙는다.
  // **비어 있는 「스크립트 N」** 으로 연다 — SQL 편집기의 새 파일과 같게(2026-10-05). 예전엔 익명 블록 틀을 채워 열었다
  const newScriptTab = () => {
    if (tabBusy) return;
    openNewTab(
      { kind: 'anon', anonNo: anonSeqRef.current++ },
      { schemaName, sourceText: '', originalSourceText: '' },
      { front: true },
    );
  };

  const revertEdits = () => {
    setSourceText(originalSourceText);
  };

  // 컴파일 결과를 읽어 판정한다. **객체를 찾았고 VALID 일 때만 성공**이다 —
  // 오류 0건만 보고 성공이라 하면, 이름·소유자가 어긋나 못 찾은 경우에도 「성공」이 뜬다.
  const readCompileResult = async (target) => {
    const res = await runPost(`${API_BASE}/sql-developer/execute`, {
      query: dbQueries.oracle.fetchPlsqlCompileResult(target.owner, target.type, target.name),
      ...monParams,
      usrid: 'standalone',
    });
    const rows = res.data?.rows || [];
    const pick = (r, k) => (r[k] !== undefined ? r[k] : r[k.toLowerCase()]);
    if (rows.length === 0) return { found: false, valid: false, errors: [] };
    // LEFT JOIN 이라 오류가 없으면 오류 칸이 빈 한 행이 온다 — 그것은 오류가 아니다
    const errors = rows.filter(r => pick(r, 'LINE') != null || pick(r, 'TEXT') != null);
    return { found: true, valid: pick(rows[0], 'STATUS') === 'VALID', errors };
  };

  // DB 의 **지금** 소스. 없으면 null. 불러올 때와 같은 GET_DDL 이라 기준선과 그대로 견줄 수 있다.
  const readCurrentSource = async (target) => {
    const res = await runPost(`${API_BASE}/sql-developer/execute`, {
      query: dbQueries.oracle.fetchPlsqlCurrentSource(target.owner, target.type, target.name),
      ...monParams,
      usrid: 'standalone',
    });
    const row = res.data?.rows?.[0];
    if (!row) return null;
    const owner = row.OWNER !== undefined ? row.OWNER : row.owner;
    const ddl = String((row.DDL_TEXT !== undefined ? row.DDL_TEXT : row.ddl_text) ?? '').trim();
    return { owner, ddl, key: plsqlKey(owner, target.type, target.name) };
  };

  // 덮어쓰기 **직전**의 DB 소스를 히스토리에 남긴다. 다른 도구로 만든 버전도 되돌릴 수 있게.
  // 로컬 저장소라 세션을 거치지 않는다(saveHistorySnapshot 과 같다). 실패는 **던진다** —
  // 호출자가 「보관 못 했는데 그래도 덮을까」를 물어야 한다.
  const saveBackup = (current, target) => axios.post(`${API_BASE}/sql-developer/monitoring/plsql-history`, {
    connectionId: selectedConnId,
    schemaName: current.owner,
    objectType: target.type,
    objectName: target.name,
    sourceText: current.ddl,
    note: t('monitoring:plsql.backupNote'),
    kind: 'backup',
  });

  const handleCompile = async ({ force = false } = {}) => {
    if (!selectedConnId) return;
    setCompiling(true);
    setCompileError(null);
    setCompileErrors(null);
    setConflict(null);
    setActiveResultTab('compile');

    const parsed = parsePlsqlCreate(sourceText);
    let target = null;
    let execErr = null;
    try {
      if (parsed) {
        target = parsed;

        // ① 덮어쓰기 충돌 — 연 뒤에 DB 가 바뀌었나, 또는 열지 않은 기존 객체를 덮으려 하나.
        //    **어떤 도구로 바꿨든 잡힌다**(Toad·SQL*Plus·배포 스크립트) — 잠금과 다른 점이다.
        let current = null;
        try {
          current = await readCurrentSource(target);
        } catch (err) {
          // 확인을 못 했는데 조용히 넘어가면 이 기능이 있는 의미가 없다 — 묻는다
          const go = force || await confirm({
            title: t('monitoring:plsql.conflictCheckFailedTitle'),
            message: t('monitoring:plsql.conflictCheckFailed', { message: formatApiError(err, t) }),
            confirmText: t('monitoring:plsql.compileAnyway'),
            cancelText: t('monitoring:plsql.cancel'),
            variant: 'danger',
          });
          if (!go) return;
        }
        if (current && !force) {
          const base = dbBaselineRef.current;
          const opened = base && base.key === current.key;
          if (!opened || normalizeSource(base.ddl) !== normalizeSource(current.ddl)) {
            setConflict({
              reason: opened ? 'changed' : 'exists',
              label: `${current.owner}.${target.name}`,
              current,
              baseline: opened ? base.ddl : null,
              mine: sourceText,
            });
            return;
          }
        }

        // ② 덮어쓰기 전 원본 보관 — 이 창이 쓴 것이 아닐 때만(내 것을 매번 백업하면 줄만 찬다)
        if (current) {
          const w = writtenRef.current;
          const isMine = w && w.key === current.key && normalizeSource(w.ddl) === normalizeSource(current.ddl);
          if (!isMine) {
            try {
              await saveBackup(current, target);
            } catch (err) {
              const go = await confirm({
                title: t('monitoring:plsql.backupFailedTitle'),
                message: t('monitoring:plsql.backupFailed', { message: formatApiError(err, t) }),
                confirmText: t('monitoring:plsql.overwrite'),
                cancelText: t('monitoring:plsql.cancel'),
                variant: 'danger',
              });
              if (!go) return;
            }
          }
        }

        try {
          await runPost(`${API_BASE}/sql-developer/execute`, {
            query: sourceText,
            ...monParams,
            usrid: 'standalone',
          });
        } catch (err) { execErr = err; }

        // ③ 방금 쓴 것을 새 기준선으로 — 다음 컴파일이 이것과 견준다
        if (!execErr) {
          try {
            const after = await readCurrentSource(target);
            dbBaselineRef.current = after ? { key: after.key, ddl: after.ddl } : null;
            writtenRef.current = dbBaselineRef.current;
          } catch (_e) {
            // 못 읽었으면 기준선을 비운다 — 옛 기준선을 두면 다음 컴파일이 가짜 충돌을 낸다
            dbBaselineRef.current = null;
            writtenRef.current = null;
          }
        }
      } else if (selectedObject && !isDirty) {
        // **소스를 고치지 않았을 때만** 저장된 객체를 다시 컴파일한다(의존 객체가 바뀌어 INVALID 된 경우).
        target = { type: selectedObject.type, owner: schemaName, name: selectedObject.name };
        try {
          await runPost(`${API_BASE}/sql-developer/execute`, {
            query: dbQueries.oracle.buildPlsqlCompileStatement(target.type, schemaName, target.name),
            ...monParams,
            usrid: 'standalone',
          });
        } catch (err) { execErr = err; }
      } else {
        // 편집한 원문을 CREATE 로 읽지 못했다. 예전에는 여기서 **저장된 옛 소스**를 ALTER … COMPILE 로
        // 다시 컴파일하고 「성공」이라 했다 — 편집한 내용은 DB 에 가지도 않았는데.
        setCompileError(t(selectedObject ? 'monitoring:plsql.cannotParseEdited' : 'monitoring:plsql.notCreateOrReplace'));
        return;
      }

      const label = target.owner ? `${target.owner}.${target.name}` : target.name;
      let result = null;
      try {
        result = await readCompileResult(target);
      } catch (_e) { /* 결과를 못 읽으면 아래에서 「확인 못 함」으로 말한다 */ }

      if (result) setCompileErrors(result.errors);
      if (execErr) {
        setCompileError(formatApiError(execErr, t));
      } else if (!result || !result.found) {
        // 성공이라고 말할 근거가 없다 — 0건을 성공으로 읽던 것이 이번 결함이었다
        setCompileError(t('monitoring:plsql.resultNotFound', { name: label }));
      } else if (!result.valid && result.errors.length === 0) {
        setCompileError(t('monitoring:plsql.invalidNoErrors', { name: label }));
      }

      // CREATE 가 실행됐으면 DB 에 이 원문이 들어갔다 — 오류가 있어도 편집 기준선은 이것이다
      if (parsed && !execErr) setOriginalSourceText(sourceText);
      if (result && result.valid && result.errors.length === 0) {
        saveHistorySnapshot(sourceText, target.type, target.name); // fire-and-forget
      }
      if (!execErr) await loadObjects();
    } finally {
      setCompiling(false);
    }
  };

  // 충돌에서 「DB 버전 불러오기」 — 편집한 것이 있으면 묻는다(되돌릴 수 없다)
  const loadConflictDbVersion = async () => {
    if (!conflict) return;
    if (isDirty) {
      const ok = await confirm({
        title: t('monitoring:plsql.discardEditsTitle'),
        message: t('monitoring:plsql.discardEdits'),
        confirmText: t('monitoring:plsql.loadDbVersion'),
        cancelText: t('monitoring:plsql.cancel'),
        variant: 'danger',
      });
      if (!ok) return;
    }
    const { current } = conflict;
    setSourceText(current.ddl);
    setOriginalSourceText(current.ddl);
    dbBaselineRef.current = { key: current.key, ddl: current.ddl };
    setConflict(null);
  };

  const handleRun = async () => {
    if (!selectedConnId) return;
    const usingTestCall = !BLOCK_RE.test(sourceText.trim()) && !!testCallText.trim();
    const textToRun = usingTestCall ? testCallText : (BLOCK_RE.test(sourceText.trim()) ? sourceText : null);

    setActiveResultTab('run');
    if (!textToRun) {
      setRunError(t('monitoring:plsql.nothingToRun'));
      setRunResult(null);
      return;
    }

    // 테스트 호출문에 REF CURSOR OUT 파라미터가 있으면 바인드 변수로 함께 전달해야
    // 백엔드가 커서를 열어 결과셋을 그리드로 fetch 한다(로컬 변수로는 불가능).
    const hasCursorBinds = usingTestCall && Object.keys(testCallBinds).length > 0;
    const bindVariables = hasCursorBinds ? { plsqlTestCall: testCallBinds } : {};

    setRunning(true);
    setRunError(null);
    setRunErrorOutput(null);
    setRunResult(null);
    const startedAt = performance.now();
    try {
      const res = await runPost(`${API_BASE}/sql-developer/plsql/execute`, {
        query: textToRun,
        ...monParams,
        usrid: 'standalone',
        bindVariables,
      });
      setRunResult({ ...res.data, elapsed: Math.round(performance.now() - startedAt) });
    } catch (err) {
      const msg = formatApiError(err, t);
      setRunError(msg);
      // 실패해도 출력이 있으면 함께 보여준다 — 예외 직전까지의 진행 상황이다.
      const partial = err.response?.data?.outputLines;
      setRunErrorOutput(Array.isArray(partial) && partial.length > 0 ? partial : null);
    } finally {
      setRunning(false);
    }
  };

  // PACKAGE/PACKAGE BODY 소스 내부 서브프로그램 개요 — PROCEDURE/FUNCTION 로 시작하는 줄만 수집.
  const sourceOutline = useMemo(() => {
    if (!selectedObject || (selectedObject.type !== 'PACKAGE' && selectedObject.type !== 'PACKAGE BODY')) {
      return [];
    }
    const items = [];
    sourceText.split('\n').forEach((line, idx) => {
      const m = SUBPROGRAM_RE.exec(line);
      if (m) items.push({ kind: m[1].toUpperCase(), name: m[2], line: idx + 1 });
    });
    return items;
  }, [sourceText, selectedObject]);

  const jumpToOutlineItem = (item) => {
    const view = mainEditorViewRef.current;
    if (!view) return;
    try {
      const targetLine = Math.min(Math.max(item.line, 1), view.state.doc.lines);
      const lineInfo = view.state.doc.line(targetLine);
      view.dispatch({
        selection: { anchor: lineInfo.from, head: lineInfo.to },
        scrollIntoView: true,
      });
      view.focus();
    } catch (_e) { /* 문서가 짧아졌거나 라인 계산 실패 — 조용히 무시 */ }
  };

  // INVALID 오브젝트 일괄 재컴파일 — 스탠드얼론(프로시저/함수/트리거) 먼저, PACKAGE, PACKAGE BODY 순.
  // ALTER ... COMPILE 자체는 에러가 있어도 예외를 던지지 않으므로 매번 ALL_ERRORS 로 성공 여부를 재확인한다.
  const RECOMPILE_ORDER = { PROCEDURE: 0, FUNCTION: 0, TRIGGER: 0, PACKAGE: 1, 'PACKAGE BODY': 2 };

  const compileObjectSilently = async (obj) => {
    try {
      const alterSql = dbQueries.oracle.buildPlsqlCompileStatement(obj.type, schemaName, obj.name);
      await runPost(`${API_BASE}/sql-developer/execute`, {
        query: alterSql,
        ...monParams,
        usrid: 'standalone',
      });
      const errRes = await runPost(`${API_BASE}/sql-developer/execute`, {
        query: dbQueries.oracle.fetchPlsqlErrors(schemaName, obj.type, obj.name),
        ...monParams,
        usrid: 'standalone',
      });
      const errorCount = (errRes.data?.rows || []).length;
      return { ...obj, ok: errorCount === 0, errorCount };
    } catch (err) {
      return { ...obj, ok: false, errorCount: null, message: formatApiError(err, t) };
    }
  };

  const handleBulkRecompile = async () => {
    if (!selectedConnId || invalidObjects.length === 0 || bulkRecompiling) return;
    setBulkRecompiling(true);
    setBulkResult(null);

    const ordered = [...invalidObjects].sort((a, b) => (RECOMPILE_ORDER[a.type] ?? 0) - (RECOMPILE_ORDER[b.type] ?? 0));
    setBulkProgress({ done: 0, total: ordered.length });

    const resultMap = new Map();
    for (let i = 0; i < ordered.length; i++) {
      const r = await compileObjectSilently(ordered[i]);
      resultMap.set(`${r.type}-${r.name}`, r);
      setBulkProgress({ done: i + 1, total: ordered.length });
    }
    // 2차 패스 — 의존성 순서(예: PACKAGE 본문이 다른 PACKAGE 를 참조) 때문에 1차에서
    // 실패한 항목이 있으면 나머지가 먼저 재컴파일된 상태에서 한 번 더 시도해준다.
    const stillInvalid = [...resultMap.values()].filter(r => !r.ok);
    for (const obj of stillInvalid) {
      const r = await compileObjectSilently(obj);
      resultMap.set(`${r.type}-${r.name}`, r);
    }

    const items = [...resultMap.values()];
    setBulkResult({
      successCount: items.filter(r => r.ok).length,
      failCount: items.filter(r => !r.ok).length,
      items,
    });
    setBulkRecompiling(false);
    await loadObjects();
  };

  // 의존성 트리 — "이 오브젝트가 참조하는 것" / "이 오브젝트를 참조하는 것" 양방향 조회. 탭을 열 때 지연 로드.
  const loadDependencies = useCallback(async () => {
    if (!selectedConnId || !selectedObject) return;
    setDepLoading(true);
    setDepError(null);
    try {
      const [dependsRes, dependentsRes] = await Promise.all([
        runPost(`${API_BASE}/sql-developer/execute`, {
          query: dbQueries.oracle.fetchPlsqlDependsOn(schemaName, selectedObject.type, selectedObject.name),
          ...monParams,
          usrid: 'standalone',
        }),
        runPost(`${API_BASE}/sql-developer/execute`, {
          query: dbQueries.oracle.fetchPlsqlDependents(schemaName, selectedObject.type, selectedObject.name),
          ...monParams,
          usrid: 'standalone',
        }),
      ]);
      setDependsOn(dependsRes.data?.rows || []);
      setDependents(dependentsRes.data?.rows || []);
    } catch (err) {
      setDepError(formatApiError(err, t));
    } finally {
      setDepLoading(false);
    }
  }, [runPost, monParams, selectedConnId, schemaName, selectedObject]);

  const openDependenciesTab = () => {
    setActiveResultTab('deps');
    if (dependsOn === null && !depLoading) loadDependencies();
  };

  // 로컬 편집 히스토리 — 컴파일 성공마다 스냅샷을 저장해두고(백엔드 monitoring.db), 필요하면 불러온다.
  // 저장 실패는 컴파일 성공 흐름을 막지 않도록 조용히 무시한다.
  const saveHistorySnapshot = async (text, objectType, objectName) => {
    if (!selectedConnId || !objectType || !objectName) return;
    try {
      await axios.post(`${API_BASE}/sql-developer/monitoring/plsql-history`, {
        connectionId: selectedConnId,     // 편집 이력은 로컬 저장소 — 세션과 무관하다
        schemaName,
        objectType,
        objectName,
        sourceText: text,
        note: t('monitoring:plsql.compileOk'),
      });
    } catch (_err) { /* 히스토리 저장 실패는 조용히 무시 */ }
  };

  const loadHistory = useCallback(async () => {
    if (!selectedConnId || !selectedObject) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/sql-developer/monitoring/plsql-history/list`, {
        params: {
          connectionId: selectedConnId,   // 편집 이력은 로컬 저장소 — 세션과 무관하다
          schemaName,
          objectType: selectedObject.type,
          objectName: selectedObject.name,
        },
      });
      setHistoryList(res.data?.data || []);
    } catch (_err) {
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedConnId, schemaName, selectedObject]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadHistory();
  };

  const loadHistorySnapshot = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/sql-developer/monitoring/plsql-history/${id}`);
      const text = res.data?.source_text;
      if (typeof text === 'string') {
        setSourceText(text);
        setShowHistory(false);
      }
    } catch (_err) { /* 조회 실패 — 목록은 그대로 두고 사용자가 재시도하게 둔다 */ }
  };

  // SQLite 의 datetime('now') 는 'YYYY-MM-DD HH:MM:SS'(UTC, 공백 구분) — Safari 등에서
  // Date 파싱이 불안정하므로 ISO 8601로 정규화 후 로컬 타임존으로 표시한다.
  const fmtHistoryTime = (s) => {
    if (!s) return '-';
    try { return new Date(s.replace(' ', 'T') + 'Z').toLocaleString(); }
    catch (_e) { return s; }
  };

  // SQLEditor(CodeMirror)는 Ctrl+Enter를 onExecute prop으로 자체 처리하므로,
  // 여기서는 SQLEditor가 다루지 않는 F5(실행)만 컨테이너 레벨에서 가로챈다.
  // (App.js의 전역 F5="전체 실행" 핸들러는 이 모달이 열려 있으면 스스로 건너뛴다.)
  const handleContainerKeyDown = (e) => {
    if (e.key === 'F5') {
      e.preventDefault();
      handleRun();
    }
  };

  const filteredObjects = useMemo(() => {
    const q = objectFilter.trim().toUpperCase();
    return objects.filter(o => !q || (o.name || '').includes(q));
  }, [objects, objectFilter]);

  const groupedObjects = useMemo(() => {
    const groups = {};
    filteredObjects.forEach(o => {
      if (!groups[o.type]) groups[o.type] = [];
      groups[o.type].push(o);
    });
    return groups;
  }, [filteredObjects]);

  if (!isOpen) return null;

  const canRunAsBlock = BLOCK_RE.test(sourceText.trim());
  const showTestCallPanel = selectedObject && (selectedObject.type === 'PROCEDURE' || selectedObject.type === 'FUNCTION');

  return (
    <AppModal windowed
      open
      onClose={onClose}
      icon={<FiCode size={14} />}
      title={t('monitoring:plsql.title')}
      overlayClassName="plsqled-overlay"
      className="plsqled-modal"
      headerClassName="plsqled-header"
      closeClassName="plsqled-close"
      /* 제목줄에는 지금 편집하는 것만 — 연결·세션·스키마는 SQL 편집처럼 편집기 툴바 줄의 오른쪽 끝으로 옮겼다(2026-11-02) */
      headerExtra={oracleConns.length > 0 ? (
        <span className="plsqled-current-label" title={currentLabel()}>
          <span className="plsqled-current-text">{currentLabel()}</span>
          {showDirty && <span className="plsqled-dirty-badge">{t('monitoring:plsql.modified')}</span>}
        </span>
      ) : null}
    >

        {oracleConns.length === 0 ? (
          <div className="plsqled-empty-state">
            <FiAlertTriangle size={28} />
            <span>{t('monitoring:plsql.oracleOnly')}</span>
          </div>
        ) : (
          <div className="plsqled-body">
            {/* Sidebar — 오브젝트 목록 */}
            <div className="plsqled-sidebar">
              <div className="plsqled-sidebar-toolbar">
                {/* 「새 익명 블록」은 편집기 툴바(히스토리 왼편)로 옮겼다 — 편집기에 대한 동작이다 */}
                <span className="plsqled-sidebar-title">{t('monitoring:plsql.objects')}</span>
                <button
                  className="plsqled-icon-btn"
                  onClick={loadObjects}
                  disabled={objectsLoading}
                  title={t('monitoring:plsql.refreshObjects')}
                >
                  <FiRefreshCw size={13} className={objectsLoading ? 'plsqled-spin' : ''} />
                </button>
                {invalidObjects.length > 0 && (
                  <button
                    className="plsqled-icon-btn plsqled-recompile-btn"
                    onClick={handleBulkRecompile}
                    disabled={bulkRecompiling}
                    title={t('monitoring:plsql.recompileAll', { count: invalidObjects.length })}
                  >
                    {bulkRecompiling ? (
                      <span className="plsqled-recompile-progress">{bulkProgress.done}/{bulkProgress.total}</span>
                    ) : (
                      <>
                        <FiCheckCircle size={13} />
                        <span className="plsqled-recompile-count">{invalidObjects.length}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              {bulkResult && (
                <div className={`plsqled-bulk-result ${bulkResult.failCount > 0 ? 'warn' : 'ok'}`}>
                  {t('monitoring:plsql.bulkDone', { ok: bulkResult.successCount, fail: bulkResult.failCount })}
                  {bulkResult.failCount > 0 && (
                    <ul className="plsqled-bulk-result-list">
                      {bulkResult.items.filter(r => !r.ok).map(r => (
                        <li key={`${r.type}-${r.name}`}>
                          {r.type} {r.name}{r.errorCount != null ? ` — ${t('monitoring:plsql.errorCount', { count: r.errorCount })}` : (r.message ? ` — ${r.message}` : '')}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="plsqled-search">
                <FiSearch size={12} />
                <input
                  type="text"
                  placeholder={t('monitoring:plsql.searchPlaceholder')}
                  value={objectFilter}
                  onChange={(e) => setObjectFilter(e.target.value)}
                />
              </div>
              <div className="plsqled-object-list">
                {objectsLoading && objects.length === 0 && (
                  <div className="plsqled-list-status">{t('monitoring:plsql.querying')}</div>
                )}
                {objectsError && (
                  <div className="plsqled-list-status error">{t('monitoring:plsql.loadFailed', { message: objectsError })}</div>
                )}
                {!objectsLoading && !objectsError && filteredObjects.length === 0 && (
                  <div className="plsqled-list-status">
                    {objects.length === 0 ? t('monitoring:plsql.noObjects', { schema: schemaName }) : t('monitoring:plsql.noSearchResult')}
                  </div>
                )}
                {TYPE_ORDER.filter(t => groupedObjects[t]?.length > 0).map(type => (
                  <div key={type} className="plsqled-object-group">
                    <div className="plsqled-object-group-label">{type} ({groupedObjects[type].length})</div>
                    {groupedObjects[type].map(obj => (
                      <button
                        key={`${obj.type}-${obj.name}`}
                        className={`plsqled-object-row ${selectedObject?.type === obj.type && selectedObject?.name === obj.name ? 'active' : ''}`}
                        onClick={() => selectObject(obj)}
                        disabled={tabBusy}
                      >
                        <span
                          className={`plsqled-status-dot ${obj.status === 'VALID' ? 'valid' : 'invalid'}`}
                          title={obj.status}
                        />
                        <span className="plsqled-object-name">{obj.name}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Main — 편집기 + 결과 */}
            <div className="plsqled-main" onKeyDown={handleContainerKeyDown}>
              {/* 1행 — SQL 편집의 편집기 툴바와 같은 줄(2026-11-02 요청): 프로젝트 선택 · 검색 · 도구 · (오른쪽 끝) 연결·세션.
                  모양은 App 의 renderEditorToolbar 와 같은 컴포넌트·클래스(ProjectPicker · EditorSearchBar · editor-action-*)다 */}
              <div className="editor-action-group plsqled-topbar">
                <ProjectPicker inline projects={plsqlProjects} activeProjectId={activeProjectId}
                  onSwitchProject={switchProject} onCloseProject={closeProject} />
                <div className="editor-action-subgroup">
                  <EditorSearchBar search={editorSearch} />
                </div>
                <span className="editor-action-sep-v" />
                <div className="editor-action-subgroup">
                  <button type="button" className="editor-action-btn" onClick={openFiles} title={t('monitoring:plsql.openFile')}><FiFolder size={14} /></button>
                  <button type="button" className="editor-action-btn" onClick={saveTabToFile} disabled={!sourceText} title={t('monitoring:plsql.saveFile')}><FiSave size={14} /></button>
                  <button type="button" className="editor-action-btn" onClick={() => saveProject(false)} title={t('monitoring:plsql.projectSaveHint')}><FiPackage size={14} /></button>
                  <input ref={fileInputRef} type="file" multiple accept={FILE_ACCEPT} style={{ display: 'none' }} onChange={onFilesPicked} />
                </div>
                <span className="editor-action-sep-v" />
                <div className="editor-action-subgroup">
                  {selectedObject && isDirty && (
                    <button type="button" className="detach-inline-btn" onClick={revertEdits} title={t('monitoring:plsql.revert')}>
                      {t('monitoring:plsql.revertShort')}
                    </button>
                  )}
                  {selectedObject && (
                    <button
                      type="button"
                      className={`detach-inline-btn ${showHistory ? 'active' : ''}`}
                      onClick={toggleHistory}
                      title={t('monitoring:plsql.historyHint')}
                      aria-pressed={showHistory}
                    >
                      <FiClock size={13} /> {t('monitoring:plsql.history')}
                    </button>
                  )}
                  <button type="button" className="detach-inline-btn" onClick={handleCompile} disabled={compiling || objectLoading}>
                    <FiCheckCircle size={13} /> {t('monitoring:plsql.compile')}
                  </button>
                  <button
                    type="button"
                    className="plsqled-btn-primary"
                    onClick={handleRun}
                    disabled={running || objectLoading || (!canRunAsBlock && !testCallText.trim())}
                  >
                    <FiPlay size={13} /> {t('monitoring:plsql.run')}
                  </button>
                </div>
                {/* 연결·세션 — 이 줄의 오른쪽 끝(SQL 편집의 세션 선택 자리). 이 창은 자기 세션을 쓰므로 SessionPicker 가 아니라 OwnConnTarget */}
                <div className="editor-action-subgroup right-align">
                  <label className="plsqled-schema-label">
                    {t('monitoring:plsql.schema')}
                    <input
                      className="plsqled-schema-input"
                      value={schemaName}
                      onChange={(e) => setSchemaName(e.target.value.toUpperCase())}
                      onBlur={loadObjects}
                      spellCheck={false}
                    />
                  </label>
                  <OwnConnTarget
                    conns={mon.conns} connId={mon.connId} onConnChange={mon.setConnId}
                    sessionId={mon.sessionId} session={mon.session}
                    sessionError={mon.sessionError} creating={mon.creating}
                    onPickSession={mon.pickSession}
                  />
                </div>
              </div>
              {/* 2행 — 탭 줄(SQL 편집의 renderFileTabsRow 와 같은 FileTabs · file-tabs-container). 바쁜 동안(컴파일·실행·불러오기)은 못 옮긴다 */}
              <div className="file-tabs-container plsqled-filetabs-row">
                <FileTabs
                  openFiles={visibleTabs.map(tb => ({ id: tb.id, name: tabLabel(tb), isDirty: tabIsDirty(tb), projectId: tb.projectId || null }))}
                  activeFileId={activeTabId}
                  onTabClick={switchTab}
                  onTabClose={closeTab}
                  onNewTab={newScriptTab}
                  onTabRename={renameTab}
                  onCloseTabs={closeTabs}
                  onTabReorder={reorderTab}
                />
              </div>

              <div className="plsqled-main-body">
              {/* 위(편집) · 아래(결과) — 막대를 끌어 높이를 바꾸고, 결과 칸은 통째로 접는다.
                  접어도 구조를 바꾸지 않는다(편집기가 새로 만들어지면 커서·되돌리기 이력이 사라진다) — 위 칸 높이만 바꾼다 */}
              <div className="plsqled-split-host" ref={splitHostRef}>
              <VerticalSplit
                size={resultPane.collapsed
                  ? `max(${RESULT_MIN_H}px, calc(100% - ${RESULT_COLLAPSED_H}px))`
                  : `max(${RESULT_MIN_H}px, calc(100% - ${resultPane.height + RESIZER_H}px))`}
                onChange={onSplitChange}
                minSize={RESULT_MIN_H}
                resizerStyle={resultPane.collapsed ? { display: 'none' } : undefined}
                pane1Style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                pane2Style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
              >
              <div className="plsqled-upper">
              {showHistory && (
                <div className="plsqled-history-panel">
                  <div className="plsqled-history-panel-title">
                    {t('monitoring:plsql.historyNote')}
                  </div>
                  {historyLoading && <div className="plsqled-result-status">{t('monitoring:plsql.loading')}</div>}
                  {!historyLoading && historyList.length === 0 && (
                    <div className="plsqled-result-status">{t('monitoring:plsql.noHistory')}</div>
                  )}
                  {!historyLoading && historyList.length > 0 && (
                    <ul className="plsqled-history-list">
                      {historyList.map(h => (
                        <li key={h.id}>
                          <button className="plsqled-history-item" onClick={() => loadHistorySnapshot(h.id)}>
                            <span className="plsqled-history-item-time">{fmtHistoryTime(h.created_at)}</span>
                            <span className="plsqled-history-item-len">{t('monitoring:plsql.charCount', { n: h.source_len })}</span>
                            {h.kind === 'backup' && <span className="plsqled-history-item-kind">{t('monitoring:plsql.backupBadge')}</span>}
                            {h.note && <span className="plsqled-history-item-note">{h.note}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {sourceOutline.length > 0 && (
                <div className="plsqled-outline-bar">
                  <span className="plsqled-outline-label">{t('monitoring:plsql.overview')}</span>
                  {sourceOutline.map((item, idx) => (
                    <button
                      key={`${item.kind}-${item.name}-${idx}`}
                      type="button"
                      className={`plsqled-outline-chip ${item.kind === 'FUNCTION' ? 'function' : 'procedure'}`}
                      onClick={() => jumpToOutlineItem(item)}
                      title={t('monitoring:plsql.gotoLine', { kind: item.kind, name: item.name, line: item.line })}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}

              {/* 편집기는 **늘** 보인다(2026-10-05) — 예전엔 본문이 비면 안내 문구로 바뀌어, 빈 탭은 쓸 수가 없었고
                  내용을 다 지우면 편집기가 사라졌다 */}
              {(
                <div className="plsqled-editor-wrap">
                  <SQLEditor
                    key={activeTabId}
                    query={sourceText}
                    setQuery={setSourceText}
                    onExecute={handleCompile}
                    onExecutePlan={() => {}}
                    onViewSchemaInfoByEditor={() => {}}
                    editorRef={mainEditorViewRef}
                    readOnly={objectLoading}
                    onFind={editorSearch.find}
                    onFindNav={editorSearch.nav}
                  />
                </div>
              )}

              {showTestCallPanel && (
                <div className={`plsqled-testcall ${testCallOpen ? 'open' : 'collapsed'}`}>
                  <div className="plsqled-testcall-header">
                    <button
                      type="button"
                      className="plsqled-testcall-toggle"
                      onClick={() => setTestCallOpen(v => !v)}
                      aria-expanded={testCallOpen}
                    >
                      <FiChevronRight size={13} className="plsqled-testcall-chevron" />
                      <span>{t('monitoring:plsql.testCall')}</span>
                      {/* 접혀 있어도 실행(F5)은 이것으로 돈다 — 무엇이 돌지 보이게 한다 */}
                      {!testCallOpen && testCallText.trim() && (
                        <code className="plsqled-testcall-peek">{testCallText.trim().split(/\r?\n/)[0]}</code>
                      )}
                    </button>
                    <button
                      className="plsqled-btn-secondary"
                      onClick={() => handleGenerateTestCall(selectedObject)}
                      title={t('monitoring:plsql.generateCall')}
                    >
                      <FiRefreshCw size={11} /> {t('monitoring:plsql.autoGenerate')}
                    </button>
                  </div>
                  {testCallOpen && testCallArgs.length > 0 && (
                    <div className="plsqled-testcall-params">
                      {testCallArgs.map(a => (
                        <label key={a.name} className="plsqled-testcall-param">
                          <span className="plsqled-testcall-param-name">
                            {a.name} <em>{a.inOut}</em>
                          </span>
                          <input
                            type="text"
                            className="plsqled-testcall-param-input"
                            placeholder={a.dataType}
                            value={testCallValues[a.name] || ''}
                            onChange={(e) => setTestCallValues(v => ({ ...v, [a.name]: e.target.value }))}
                            disabled={a.inOut === 'OUT'}
                            spellCheck={false}
                          />
                        </label>
                      ))}
                      <button className="plsqled-btn-secondary plsqled-testcall-apply" onClick={applyTestCallValues} title={t('monitoring:plsql.applyInputs')}>
                        {t('monitoring:plsql.applyValues')}
                      </button>
                    </div>
                  )}
                  {testCallOpen && (
                  <div className="plsqled-testcall-editor-wrap">
                    <SQLEditor
                      query={testCallText}
                      setQuery={setTestCallText}
                      onExecute={handleCompile}
                      onExecutePlan={() => {}}
                      onViewSchemaInfoByEditor={() => {}}
                      minimap={false}
                    />
                  </div>
                  )}
                </div>
              )}
              </div>

              <div className={`plsqled-results${resultPane.collapsed ? ' collapsed' : ''}`}>
                <div className="plsqled-result-tabs">
                  <button
                    className={`plsqled-result-tab ${activeResultTab === 'lint' ? 'active' : ''}`}
                    onClick={() => pickResultTab('lint')}
                    title={t('monitoring:plsql.lintHint')}
                  >
                    {t('monitoring:plsql.lintResult')}
                    {lintIssues.length > 0 && (
                      <span className={`plsqled-badge ${lintErrorCount > 0 ? 'error' : 'warn'}`}>{lintIssues.length}</span>
                    )}
                  </button>
                  <button
                    className={`plsqled-result-tab ${activeResultTab === 'compile' ? 'active' : ''}`}
                    onClick={() => pickResultTab('compile')}
                  >
                    {t('monitoring:plsql.compileResult')}
                    {compileErrors && compileErrors.length > 0 && (
                      <span className="plsqled-badge error">{compileErrors.length}</span>
                    )}
                  </button>
                  <button
                    className={`plsqled-result-tab ${activeResultTab === 'run' ? 'active' : ''}`}
                    onClick={() => pickResultTab('run')}
                  >
                    {t('monitoring:plsql.runResult')}
                    {runResult?.outputLines?.length > 0 && (
                      <span className="plsqled-badge">{runResult.outputLines.length}</span>
                    )}
                  </button>
                  {selectedObject && (
                    <button
                      className={`plsqled-result-tab ${activeResultTab === 'deps' ? 'active' : ''}`}
                      onClick={() => { setResultCollapsed(false); openDependenciesTab(); }}
                    >
                      {t('monitoring:plsql.dependencies')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="plsqled-result-collapse"
                    onClick={() => setResultCollapsed(!resultPane.collapsed)}
                    aria-expanded={!resultPane.collapsed}
                    title={t(resultPane.collapsed ? 'monitoring:plsql.resultExpand' : 'monitoring:plsql.resultCollapse')}
                    aria-label={t(resultPane.collapsed ? 'monitoring:plsql.resultExpand' : 'monitoring:plsql.resultCollapse')}
                  >
                    {resultPane.collapsed ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                  </button>
                </div>
                {/* 덮어쓰기 충돌의 단추는 결과 칸 맨 위에 고정 — 아래 비교(diff)가 길어도 스크롤 없이 누른다 */}
                {!resultPane.collapsed && activeResultTab === 'compile' && !compiling && conflict && (
                  <div className="plsqled-conflict-bar">
                    <div className="plsqled-conflict-head">
                      <FiAlertTriangle size={14} />
                      <b>{t(conflict.reason === 'changed' ? 'monitoring:plsql.conflictChangedTitle' : 'monitoring:plsql.conflictExistsTitle')}</b>
                    </div>
                    <div className="plsqled-conflict-actions">
                      <button type="button" className="plsqled-btn-secondary" onClick={() => setConflict(null)}>
                        {t('monitoring:plsql.cancel')}
                      </button>
                      <button type="button" className="plsqled-btn-secondary" onClick={loadConflictDbVersion}>
                        {t('monitoring:plsql.loadDbVersion')}
                      </button>
                      <button type="button" className="plsqled-btn-danger" onClick={() => handleCompile({ force: true })}>
                        {t('monitoring:plsql.overwrite')}
                      </button>
                    </div>
                  </div>
                )}
                {!resultPane.collapsed && (
                <div className="plsqled-result-body" key={activeResultTab}>
                  {activeResultTab === 'lint' && (
                    lintIssues.length === 0 ? (
                      <div className="plsqled-result-success"><FiCheckCircle size={13} /> {sourceText.trim() ? t('monitoring:plsql.lintOk') : t('monitoring:plsql.lintEmpty')}</div>
                    ) : (
                      <table className="plsqled-error-table plsqled-lint-table">
                        <thead>
                          <tr><th>LINE</th><th>POS</th><th>{t('monitoring:plsql.content')}</th></tr>
                        </thead>
                        <tbody>
                          {lintIssues.map((iss, idx) => (
                            <tr key={idx} className={`plsqled-lint-${iss.severity}`} onClick={() => jumpToLine(iss.line, iss.col)} title={t('monitoring:plsql.gotoLineShort')}>
                              <td>{iss.line}</td>
                              <td>{iss.col}</td>
                              <td>
                                <span className="plsqled-lint-sev">{t(`monitoring:plsql.lintSev.${iss.severity}`)}</span>
                                {t(`monitoring:plsql.lint.${iss.code}`, iss.params || {})}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                  {activeResultTab === 'compile' && (
                    <>
                      {compiling && <div className="plsqled-result-status">{t('monitoring:plsql.compiling')}</div>}
                      {!compiling && conflict && (
                        <div className="plsqled-conflict">
                          <div className="plsqled-conflict-msg">
                            {t(conflict.reason === 'changed' ? 'monitoring:plsql.conflictChanged' : 'monitoring:plsql.conflictExists', { name: conflict.label })}
                          </div>
                          <div className="plsqled-conflict-legend">
                            {t(conflict.reason === 'changed' ? 'monitoring:plsql.conflictDiffChanged' : 'monitoring:plsql.conflictDiffExists')}
                          </div>
                          {/* changed: 연 시점 → DB 지금 (남이 바꾼 것) / exists: DB 지금 → 내가 쓸 것 (내가 덮을 것) */}
                          <pre className="plsqled-conflict-diff">
                            {withContext(conflict.reason === 'changed'
                              ? lineDiff(conflict.baseline, conflict.current.ddl)
                              : lineDiff(conflict.current.ddl, conflict.mine), 3).map((d, i) => (
                              d.type === 'gap'
                                ? <div key={i} className="plsqled-diff-gap">{t('monitoring:plsql.diffGap', { count: d.count })}</div>
                                : <div key={i} className={`plsqled-diff-${d.type}`}>{d.type === 'add' ? '+ ' : d.type === 'del' ? '- ' : '  '}{d.text}</div>
                            ))}
                          </pre>
                          <div className="plsqled-conflict-note"><FiInfo size={12} /> {t('monitoring:plsql.conflictBackupNote')}</div>
                        </div>
                      )}
                      {!compiling && compileError && (
                        <div className="plsqled-result-error"><FiAlertTriangle size={13} /> {compileError}</div>
                      )}
                      {!compiling && compileErrors && compileErrors.length === 0 && !compileError && (
                        <div className="plsqled-result-success"><FiCheckCircle size={13} /> {t('monitoring:plsql.compileOkNoErrors')}</div>
                      )}
                      {!compiling && compileErrors && compileErrors.length > 0 && (
                        <table className="plsqled-error-table">
                          <thead>
                            <tr><th>LINE</th><th>POS</th><th>{t('monitoring:plsql.content')}</th></tr>
                          </thead>
                          <tbody>
                            {compileErrors.map((e, idx) => (
                              <tr key={idx}>
                                <td>{e.LINE ?? e.line}</td>
                                <td>{e.POSITION ?? e.position}</td>
                                <td>{e.TEXT ?? e.text}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {!compiling && !conflict && !compileError && !compileErrors && (
                        <div className="plsqled-result-status">{t('monitoring:plsql.compileHint')}</div>
                      )}
                    </>
                  )}
                  {activeResultTab === 'run' && (
                    <>
                      {running && <div className="plsqled-result-status">{t('monitoring:plsql.running')}</div>}
                      {!running && runError && (
                        <div className="plsqled-result-error"><FiAlertTriangle size={13} /> {runError}</div>
                      )}
                      {runErrorOutput && (
                        <>
                          <div className="plsqled-output-label"><FiTerminal size={12} /> DBMS_OUTPUT ({t('monitoring:plsql.beforeError')})</div>
                          <pre className="plsqled-output">{runErrorOutput.join('\n')}</pre>
                        </>
                      )}
                      {!running && runResult && (
                        <>
                          <div className="plsqled-result-meta">
                            <FiCheckCircle size={13} /> {runResult.message} · {runResult.elapsed}ms
                          </div>
                          <div className="plsqled-output-label"><FiTerminal size={12} /> DBMS_OUTPUT</div>
                          {runResult.outputLines && runResult.outputLines.length > 0 ? (
                            <pre className="plsqled-output">{runResult.outputLines.join('\n')}</pre>
                          ) : (
                            <div className="plsqled-result-status">{t('monitoring:plsql.noOutput')}</div>
                          )}
                          {runResult.outputTruncated && (
                            <div className="plsqled-result-status">{t('monitoring:plsql.outputTruncated')}</div>
                          )}
                          {runResult.cursorResults && runResult.cursorResults.length > 0 && (
                            <div className="plsqled-cursor-results">
                              {runResult.cursorResults.map((cr, idx) => (
                                <div key={cr.name || idx} className="plsqled-cursor-result">
                                  <div className="plsqled-cursor-result-title">
                                    <FiGrid size={12} /> {cr.name} — {t('monitoring:plsql.rowCount', { n: cr.rows.length })}{cr.truncated ? '+' : ''}
                                  </div>
                                  <div className="plsqled-cursor-result-grid-wrap">
                                    <table className="plsqled-cursor-result-grid">
                                      <thead>
                                        <tr>{(cr.metaData || []).map(m => <th key={m.name}>{m.name}</th>)}</tr>
                                      </thead>
                                      <tbody>
                                        {cr.rows.map((row, rIdx) => (
                                          <tr key={rIdx}>
                                            {(cr.metaData || []).map(m => <td key={m.name}>{row[m.name] == null ? '' : String(row[m.name])}</td>)}
                                          </tr>
                                        ))}
                                        {cr.rows.length === 0 && (
                                          <tr><td colSpan={(cr.metaData || []).length || 1} className="plsqled-empty-note">{t('monitoring:plsql.noResult')}</td></tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      {!running && !runError && !runResult && (
                        <div className="plsqled-result-status">{t('monitoring:plsql.runHint')}</div>
                      )}
                    </>
                  )}
                  {activeResultTab === 'deps' && (
                    <>
                      {depLoading && <div className="plsqled-result-status">{t('monitoring:plsql.querying')}</div>}
                      {!depLoading && depError && (
                        <div className="plsqled-result-error"><FiAlertTriangle size={13} /> {depError}</div>
                      )}
                      {!depLoading && !depError && dependsOn !== null && (
                        <div className="plsqled-deps">
                          <div className="plsqled-deps-col">
                            <div className="plsqled-deps-col-title">{t('monitoring:plsql.dependsOn', { n: dependsOn.length })}</div>
                            <ul className="plsqled-deps-list">
                              {dependsOn.map((d, i) => (
                                <li key={i}>
                                  <span className="plsqled-deps-type">{d.REFERENCED_TYPE ?? d.referenced_type}</span>
                                  {d.REFERENCED_OWNER ?? d.referenced_owner}.{d.REFERENCED_NAME ?? d.referenced_name}
                                </li>
                              ))}
                              {dependsOn.length === 0 && <li className="plsqled-deps-empty">{t('monitoring:plsql.none')}</li>}
                            </ul>
                          </div>
                          <div className="plsqled-deps-col">
                            <div className="plsqled-deps-col-title">{t('monitoring:plsql.dependents', { n: (dependents || []).length })}</div>
                            <ul className="plsqled-deps-list">
                              {(dependents || []).map((d, i) => (
                                <li key={i}>
                                  <span className="plsqled-deps-type">{d.TYPE ?? d.type}</span>
                                  {d.OWNER ?? d.owner}.{d.NAME ?? d.name}
                                </li>
                              ))}
                              {(dependents || []).length === 0 && <li className="plsqled-deps-empty">{t('monitoring:plsql.none')}</li>}
                            </ul>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}
              </div>
              </VerticalSplit>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="plsqled-footer">
          <div className="plsqled-footer-note">
            <FiInfo size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t('monitoring:plsql.ddlNote')}
          </div>
          <button className="app-btn plsqled-btn-secondary" onClick={onClose}>{t('monitoring:plsql.close')}</button>
        </div>
    </AppModal>
  );
};

export default PlsqlEditorModal;
